import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Bell, Leaf } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Footer } from "@/components/Footer";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [sel, setSel] = useState(0);
  const [restockEmail, setRestockEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setSel(0); setSubscribed(false); });
  }, [slug]);

  if (!p) return <div className="App bg-[#080808] pt-40 text-center text-[#8A8782]">Loading…</div>;
  const v = p.variants[sel];

  const addToCart = () => {
    if (v.in_stock === false) return;
    add({ lookup_key: v.lookup_key, name: p.name, size: v.size, price: v.price, image_url: p.image_url, quantity: 1 });
    toast.success(`${p.name} — ${v.size} added`);
  };
  const notifyMe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/restock", { lookup_key: v.lookup_key, email: restockEmail });
      setSubscribed(true); setRestockEmail("");
      toast.success("We'll email you when it's back");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const soldOut = v.in_stock === false;

  return (
    <div className="App bg-[#080808] pt-16">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-10">
        <Link to="/shop" className="overline inline-flex items-center gap-2 hover:text-[#C7F000] transition-colors mb-8"><ArrowLeft size={14} /> Back to catalog</Link>
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="md:sticky md:top-24 self-start">
            <div className="aspect-square bg-[#121212] overflow-hidden border border-[#1c1c1c]">
              <img src={imgUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" data-testid="product-image" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="py-4">
            <p className="overline mb-3">{p.category}</p>
            <h1 className="font-display font-light text-5xl md:text-6xl tracking-tighter leading-none" data-testid="product-name">{p.name}</h1>
            <p className="font-display italic text-xl text-[#8A8782] mt-2">{p.scientific_name}</p>
            <p className="text-3xl mt-8 mb-8" data-testid="product-price">${v.price.toFixed(2)}</p>
            <p className="text-[#8A8782] text-sm leading-relaxed max-w-md mb-10">{p.description}</p>

            <p className="overline mb-4">Select size / count</p>
            <div className="flex flex-wrap gap-3 mb-10">
              {p.variants.map((variant, i) => {
                const out = variant.in_stock === false;
                return (
                  <button key={variant.lookup_key} data-testid={`variant-${i}`} onClick={() => setSel(i)}
                    className={`px-5 py-3 text-xs border transition-colors relative ${sel === i ? "border-[#C7F000] text-[#C7F000] bg-[#C7F000]/5" : "border-[#222] text-[#8A8782] hover:border-[#444]"} ${out ? "opacity-45" : ""}`}>
                    {variant.size} — ${variant.price.toFixed(2)}{out && <span className="ml-2 text-[#D94026] uppercase tracking-wider">sold out</span>}
                  </button>
                );
              })}
            </div>

            <button data-testid="add-to-cart-btn" onClick={addToCart} disabled={soldOut}
              className="btn-toxic w-full py-5 flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed">
              <Check size={16} /> {soldOut ? "Sold Out" : "Add to Order"}
            </button>
            {soldOut && (
              subscribed ? (
                <p data-testid="restock-confirmed" className="text-[#C7F000] text-xs mt-4 inline-flex items-center gap-2"><Bell size={13} /> You're on the list — we'll email you when this size is back.</p>
              ) : (
                <form onSubmit={notifyMe} className="mt-5 border border-[#222] p-4">
                  <p className="overline mb-3 inline-flex items-center gap-2"><Bell size={13} /> Notify me when back in stock</p>
                  <div className="flex gap-3">
                    <input data-testid="restock-email" type="email" required placeholder="you@email.com" value={restockEmail} onChange={(e) => setRestockEmail(e.target.value)} className="field flex-1" />
                    <button data-testid="restock-submit" className="px-5 py-2 border border-[#C7F000] text-[#C7F000] overline hover:bg-[#C7F000]/10 transition-colors">Notify</button>
                  </div>
                </form>
              )
            )}
            <p className="text-[11px] text-[#8A8782] mt-4 leading-relaxed">Live-arrival guarantee · Overnight insulated shipping · Gut-loaded before dispatch</p>
            <p className="text-[11px] text-[#C7F000] mt-2 leading-relaxed" data-testid="bulk-note">Buy more, save more: 3+ units 5% off · 6+ 10% off · 12+ 15% off — applied automatically at checkout.</p>

            {p.care_tips && p.care_tips.length > 0 && (
              <div className="mt-12 border-t border-[#222] pt-8" data-testid="care-guide">
                <p className="overline mb-5 inline-flex items-center gap-2"><Leaf size={13} className="text-[#C7F000]" /> Care & Gut-Loading Guide</p>
                <ul className="space-y-4">
                  {p.care_tips.map((tip, i) => (
                    <li key={i} className="flex gap-4 text-sm text-[#8A8782] leading-relaxed">
                      <span className="font-display text-lg text-[#C7F000] leading-none">{String(i + 1).padStart(2, "0")}</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
